import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import s from './Financeiro.module.css';

const TIPOS = ['caixa', 'banco', 'cofre', 'outros'];
const TIPO_LABEL = { caixa: 'Caixa', banco: 'Banco', cofre: 'Cofre', outros: 'Outros' };
const BADGE_TIPO = { caixa: s.badgeCaixa, banco: s.badgeBanco, cofre: s.badgeCofre, outros: s.badgeOutros };

const VAZIO = { nome: '', tipo: 'caixa', banco: '', agencia: '', conta: '', saldo_inicial: '0',
  banco_codigo: '', agencia_dv: '', conta_dv: '', carteira: '', convenio: '', boleto_instrucoes: '',
  boleto_multa_pct: '0', boleto_juros_mes_pct: '0', serie_nfce: '', nfce_proximo_numero: '',
  integracao_boleto: 'nenhuma', cora_ambiente: 'producao', cora_client_id: '',
  cora_certificado: '', cora_chave_privada: '' };

export default function ContasFinanceiras() {
  const [contas, setContas]       = useState([]);
  const [sel, setSel]             = useState(null);
  const [form, setForm]           = useState(VAZIO);
  const [modo, setModo]           = useState('lista'); // lista | novo | editar
  const [salvando, setSalvando]   = useState(false);
  const [erro, setErro]           = useState('');
  const [sucesso, setSucesso]     = useState('');
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [coraTestando, setCoraTestando]     = useState(false);
  const [coraMsg, setCoraMsg]               = useState(null); // { ok, texto } | null
  const [coraWebhookUrl, setCoraWebhookUrl] = useState('');

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get('/financeiro/contas');
      setContas(data);
    } catch { setErro('Erro ao carregar contas.'); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const contasFiltradas = contas.filter(c => mostrarInativos || c.ativo);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const novo = () => {
    setForm(VAZIO); setSel(null); setErro(''); setSucesso(''); setCoraMsg(null); setCoraWebhookUrl(''); setModo('novo');
  };

  const editar = (c) => {
    setForm({
      nome: c.nome, tipo: c.tipo, banco: c.banco || '',
      agencia: c.agencia || '', conta: c.conta || '',
      saldo_inicial: c.saldo_inicial,
      banco_codigo: c.banco_codigo || '', agencia_dv: c.agencia_dv || '',
      conta_dv: c.conta_dv || '', carteira: c.carteira || '',
      convenio: c.convenio || '', boleto_instrucoes: c.boleto_instrucoes || '',
      boleto_multa_pct: c.boleto_multa_pct ?? '0', boleto_juros_mes_pct: c.boleto_juros_mes_pct ?? '0',
      serie_nfce: c.serie_nfce || '',
      // Em branco de propósito = "não mexer" (mantém o próximo número atual);
      // o valor atual só aparece como placeholder, não como valor preenchido.
      nfce_proximo_numero: '',
      integracao_boleto: c.integracao_boleto || 'nenhuma',
      cora_ambiente: c.cora_ambiente || 'producao',
      cora_client_id: c.cora_client_id || '',
      // Certificado/chave NUNCA voltam da API (ver contasFinanceirasController) —
      // em branco aqui = "manter o que já está salvo", só preenche pra TROCAR.
      cora_certificado: '', cora_chave_privada: '',
    });
    setSel(c);
    setErro(''); setSucesso(''); setCoraMsg(null); setCoraWebhookUrl('');
    setModo('editar');
  };

  const cancelar = () => { setModo('lista'); setSel(null); setErro(''); setSucesso(''); setCoraMsg(null); };

  const testarCora = async () => {
    setCoraTestando(true); setCoraMsg(null);
    try {
      const { data } = await api.post(`/financeiro/contas/${sel.id}/cora/testar`);
      setCoraMsg({ ok: true, texto: data.mensagem });
    } catch (err) {
      setCoraMsg({ ok: false, texto: err.response?.data?.erro || 'Erro ao testar a conexão.' });
    } finally { setCoraTestando(false); }
  };

  const registrarWebhookCora = async () => {
    setCoraTestando(true); setCoraMsg(null);
    try {
      const { data } = await api.post(`/financeiro/contas/${sel.id}/cora/webhook`);
      setCoraMsg({ ok: true, texto: data.mensagem });
      setCoraWebhookUrl(data.url);
    } catch (err) {
      setCoraMsg({ ok: false, texto: err.response?.data?.erro || 'Erro ao registrar o webhook.' });
    } finally { setCoraTestando(false); }
  };

  // Sem depender do webhook (credencial separada, nem sempre fácil de
  // achar) — confere agora mesmo os boletos em aberto contra a API do Cora,
  // usando só o certificado que já emite boleto. O sistema já faz isso
  // sozinho a cada 15min; este botão só adianta pra quem não quer esperar.
  const sincronizarCora = async () => {
    setCoraTestando(true); setCoraMsg(null);
    try {
      const { data } = await api.post(`/financeiro/contas/${sel.id}/cora/sincronizar`);
      setCoraMsg({
        ok: true,
        texto: `Verificados ${data.verificados} boleto(s) — ${data.liquidados} pago(s), ${data.cancelados} cancelado(s)${data.erros ? `, ${data.erros} com erro` : ''}.`,
      });
    } catch (err) {
      setCoraMsg({ ok: false, texto: err.response?.data?.erro || 'Erro ao sincronizar.' });
    } finally { setCoraTestando(false); }
  };

  const salvar = async () => {
    if (!form.nome.trim()) { setErro('Nome é obrigatório.'); return; }
    setErro(''); setSalvando(true);
    try {
      if (modo === 'novo') {
        await api.post('/financeiro/contas', { ...form, saldo_inicial: parseFloat(form.saldo_inicial) || 0 });
      } else {
        await api.put(`/financeiro/contas/${sel.id}`, { ...form, saldo_inicial: parseFloat(form.saldo_inicial) || 0 });
      }
      setSucesso('Salvo com sucesso!');
      setTimeout(() => setSucesso(''), 2500);
      await carregar();
      setModo('lista');
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar.');
    } finally { setSalvando(false); }
  };

  const toggleAtivo = async (c) => {
    try {
      await api.put(`/financeiro/contas/${c.id}`, { ativo: !c.ativo });
      await carregar();
    } catch { setErro('Erro ao alterar status.'); }
  };

  return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}><span>💳</span> Contas Financeiras</div>

      <div className={s.toolbar}>
        <button className={s.btnErpPrimario} onClick={novo}>+ Nova Conta</button>
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={mostrarInativos} onChange={e => setMostrarInativos(e.target.checked)} />
          Mostrar inativas
        </label>
      </div>

      {erro    && <div className={s.erroMsg}>{erro}</div>}
      {sucesso && <div className={s.sucessoMsg}>{sucesso}</div>}

      <div className={s.conteudoSplit}>
        {/* Lista */}
        <div className={`${s.painel} ${s.painelLeft}`}>
          <div className={s.painelScroll}>
            {contasFiltradas.length === 0
              ? <div style={{ padding: 16, color: '#888', fontSize: 12 }}>Nenhuma conta cadastrada.</div>
              : contasFiltradas.map(c => (
                <div
                  key={c.id}
                  className={`${s.listaItem} ${sel?.id === c.id ? s.listaItemSel : ''}`}
                  onClick={() => editar(c)}
                >
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: 13 }}>{c.nome}</div>
                    <div style={{ fontSize: 11, opacity: 0.8 }}>
                      Saldo inicial: R$ {parseFloat(c.saldo_inicial).toFixed(2).replace('.',',')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                    <span className={`${s.badge} ${BADGE_TIPO[c.tipo]}`}>{TIPO_LABEL[c.tipo]}</span>
                    <span className={`${s.badge} ${c.ativo ? s.badgeAtivo : s.badgeInativo}`}>
                      {c.ativo ? 'Ativa' : 'Inativa'}
                    </span>
                  </div>
                </div>
              ))
            }
          </div>
        </div>

        {/* Formulário */}
        <div className={s.painelRight}>
          {modo === 'lista'
            ? <div style={{ color: '#888', fontSize: 12, marginTop: 20, textAlign: 'center' }}>
                Selecione uma conta para editar ou clique em "+ Nova Conta".
              </div>
            : (
              <div className={s.form}>
                <div className={s.fieldset}>
                  <div className={s.legend}>{modo === 'novo' ? 'Nova Conta' : 'Editar Conta'}</div>

                  <div className={s.formRow}>
                    <div className={s.formGrupo} style={{ flex: 1 }}>
                      <label className={s.label}>Nome *</label>
                      <input className={`${s.input} ${s.inputLarge}`} value={form.nome}
                        onChange={e => set('nome', e.target.value)} autoFocus />
                    </div>
                    <div className={s.formGrupo}>
                      <label className={s.label}>Tipo *</label>
                      <select className={s.select} value={form.tipo} onChange={e => set('tipo', e.target.value)}>
                        {TIPOS.map(t => <option key={t} value={t}>{TIPO_LABEL[t]}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className={s.formRow}>
                    <div className={s.formGrupo} style={{ flex: 1 }}>
                      <label className={s.label}>Saldo Inicial (R$)</label>
                      <input className={s.input} value={form.saldo_inicial} type="number" step="0.01"
                        onChange={e => set('saldo_inicial', e.target.value)} style={{ width: 130 }} />
                    </div>
                  </div>

                  {form.tipo === 'caixa' && (
                    <div className={s.formRow}>
                      <div className={s.formGrupo}>
                        <label className={s.label}>Série NFC-e deste caixa</label>
                        <input className={s.input} value={form.serie_nfce} style={{ width: 80 }}
                          placeholder={'padrão'}
                          onChange={e => set('serie_nfce', e.target.value)} />
                      </div>
                      {form.serie_nfce && (
                        <div className={s.formGrupo}>
                          <label className={s.label}>Próximo número da NFC-e</label>
                          <input className={s.input} type="number" min="1" style={{ width: 110 }}
                            value={form.nfce_proximo_numero}
                            placeholder={sel?.nfce_proximo_numero != null ? String(sel.nfce_proximo_numero) : '1'}
                            onChange={e => set('nfce_proximo_numero', e.target.value)} />
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: '#888', alignSelf: 'flex-end', paddingBottom: 6, maxWidth: 340 }}>
                        Em branco = usa a série padrão da organização (Cadastro de Organização → NFC-e).
                        Só preencha se este caixa deve emitir numa série própria — a numeração de
                        cada série é independente e nunca se repete.
                        {form.serie_nfce && (
                          <div style={{ marginTop: 4 }}>
                            Se essa série já foi usada noutro sistema, informe aqui o próximo
                            número a emitir (último número já autorizado + 1), pra não duplicar
                            no SEFAZ. Depois de a primeira NFC-e sair daqui, este campo passa a
                            se atualizar sozinho a cada venda.
                          </div>
                        )}
                        {modo === 'editar' && sel?.nfce_proximo_numero != null && (
                          <div style={{ marginTop: 4 }}>
                            Próximo número atual: <strong>{sel.nfce_proximo_numero}</strong>
                            {sel.nfce_ultimo_nf != null && <> — último emitido: <strong>{sel.nfce_ultimo_nf}</strong></>}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {form.tipo === 'banco' && (
                    <>
                      <div className={s.formRow}>
                        <div className={s.formGrupo} style={{ flex: 1 }}>
                          <label className={s.label}>Banco</label>
                          <input className={`${s.input} ${s.inputLarge}`} value={form.banco}
                            placeholder="Ex: Bradesco, Itaú..." onChange={e => set('banco', e.target.value)} />
                        </div>
                      </div>
                      <div className={s.formRow}>
                        <div className={s.formGrupo}>
                          <label className={s.label}>Agência</label>
                          <input className={s.input} value={form.agencia} style={{ width: 100 }}
                            onChange={e => set('agencia', e.target.value)} />
                        </div>
                        <div className={s.formGrupo}>
                          <label className={s.label}>DV</label>
                          <input className={s.input} value={form.agencia_dv} style={{ width: 40 }} maxLength={1}
                            onChange={e => set('agencia_dv', e.target.value)} />
                        </div>
                        <div className={s.formGrupo} style={{ flex: 1 }}>
                          <label className={s.label}>Conta</label>
                          <input className={`${s.input} ${s.inputLarge}`} value={form.conta}
                            onChange={e => set('conta', e.target.value)} />
                        </div>
                        <div className={s.formGrupo}>
                          <label className={s.label}>DV</label>
                          <input className={s.input} value={form.conta_dv} style={{ width: 40 }} maxLength={1}
                            onChange={e => set('conta_dv', e.target.value)} />
                        </div>
                      </div>

                      <div className={s.formRow}>
                        <div className={s.formGrupo}>
                          <label className={s.label}>Integração de Boleto</label>
                          <select className={s.select} value={form.integracao_boleto}
                            onChange={e => { setCoraMsg(null); set('integracao_boleto', e.target.value); }}>
                            <option value="nenhuma">Nenhuma</option>
                            <option value="cnab400">CNAB400 (arquivo remessa/retorno)</option>
                            <option value="cora">Cora (API em tempo real)</option>
                          </select>
                        </div>
                      </div>

                      {form.integracao_boleto === 'cnab400' && (
                        <div className={s.formRow}>
                          <div className={s.formGrupo}>
                            <label className={s.label}>Cód. Banco (boletos)</label>
                            <input className={s.input} value={form.banco_codigo} style={{ width: 90 }} maxLength={3}
                              placeholder="237" onChange={e => set('banco_codigo', e.target.value)} />
                          </div>
                          <div className={s.formGrupo}>
                            <label className={s.label}>Carteira</label>
                            <input className={s.input} value={form.carteira} style={{ width: 70 }} maxLength={3}
                              placeholder="09" onChange={e => set('carteira', e.target.value)} />
                          </div>
                          <div className={s.formGrupo} style={{ flex: 1 }}>
                            <label className={s.label}>Convênio / Cód. empresa</label>
                            <input className={`${s.input} ${s.inputLarge}`} value={form.convenio}
                              onChange={e => set('convenio', e.target.value)} />
                          </div>
                        </div>
                      )}

                      {form.integracao_boleto === 'cora' && (
                        <>
                          <div className={s.formRow}>
                            <div className={s.formGrupo}>
                              <label className={s.label}>Ambiente</label>
                              <select className={s.select} value={form.cora_ambiente}
                                onChange={e => set('cora_ambiente', e.target.value)}>
                                <option value="producao">Produção (boletos reais)</option>
                                <option value="sandbox">Sandbox (testes)</option>
                              </select>
                            </div>
                            <div className={s.formGrupo} style={{ flex: 1 }}>
                              <label className={s.label}>Client ID</label>
                              <input className={`${s.input} ${s.inputLarge}`} value={form.cora_client_id}
                                placeholder="Copie do Cora Web → Conta → Integrações via API"
                                onChange={e => set('cora_client_id', e.target.value)} />
                            </div>
                          </div>
                          <div className={s.formRow}>
                            <div className={s.formGrupo} style={{ flex: 1 }}>
                              <label className={s.label}>
                                Certificado (.pem){sel?.cora_configurado && !form.cora_certificado ? ' — já configurado' : ''}
                              </label>
                              <textarea className={`${s.input} ${s.inputLarge}`} rows={3}
                                style={{ fontFamily: 'monospace', fontSize: 11, resize: 'vertical' }}
                                value={form.cora_certificado}
                                placeholder={sel?.cora_configurado ? 'Deixe em branco pra manter o certificado já salvo' : '-----BEGIN CERTIFICATE-----'}
                                onChange={e => set('cora_certificado', e.target.value)} />
                            </div>
                          </div>
                          <div className={s.formRow}>
                            <div className={s.formGrupo} style={{ flex: 1 }}>
                              <label className={s.label}>
                                Chave privada (.key){sel?.cora_configurado && !form.cora_chave_privada ? ' — já configurada' : ''}
                              </label>
                              <textarea className={`${s.input} ${s.inputLarge}`} rows={3}
                                style={{ fontFamily: 'monospace', fontSize: 11, resize: 'vertical' }}
                                value={form.cora_chave_privada}
                                placeholder={sel?.cora_configurado ? 'Deixe em branco pra manter a chave já salva' : '-----BEGIN PRIVATE KEY-----'}
                                onChange={e => set('cora_chave_privada', e.target.value)} />
                            </div>
                          </div>
                          <div style={{ fontSize: 11, color: '#888', maxWidth: 480, marginBottom: 6 }}>
                            Client ID, certificado e chave ficam em <strong>cora.com.br → Conta →
                            Integrações via API → Acessar Integração Direta → Ambiente de produção</strong>
                            (dois arquivos pra baixar e abrir num editor de texto — não aparecem prontos
                            na tela). Criptografados ao salvar — não voltam a aparecer em texto depois.
                          </div>

                          {modo === 'editar' && sel && (
                            <div className={s.formRow} style={{ alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                              <button type="button" className={s.btnErp} disabled={coraTestando}
                                onClick={testarCora}>
                                {coraTestando ? 'Testando…' : 'Testar conexão'}
                              </button>
                              <button type="button" className={s.btnErp} disabled={coraTestando}
                                onClick={registrarWebhookCora}>
                                Registrar webhook
                              </button>
                              <button type="button" className={s.btnErpPrimario} disabled={coraTestando}
                                onClick={sincronizarCora}>
                                {coraTestando ? 'Verificando…' : '🔄 Verificar pagamentos agora'}
                              </button>
                            </div>
                          )}
                          {modo === 'novo' && (
                            <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>
                              Salve a conta primeiro pra poder testar a conexão e registrar o webhook.
                            </div>
                          )}
                          <div style={{ fontSize: 11, color: '#888', maxWidth: 480, margin: '4px 0 10px' }}>
                            "Registrar webhook" avisa o sistema assim que o boleto for pago (instantâneo).
                            Além disso, o sistema já confere sozinho a cada 15 minutos, mesmo sem
                            webhook — "Verificar pagamentos agora" só adianta essa conferência na hora.
                          </div>
                          {coraMsg && (
                            <div style={{
                              fontSize: 12, padding: '6px 10px', borderRadius: 4, marginBottom: 8,
                              background: coraMsg.ok ? '#f0fdf4' : '#fef2f2',
                              border: `1px solid ${coraMsg.ok ? '#86efac' : '#fca5a5'}`,
                              color: coraMsg.ok ? '#166534' : '#b91c1c',
                            }}>
                              {coraMsg.texto}
                              {coraWebhookUrl && <div style={{ marginTop: 2, wordBreak: 'break-all' }}>{coraWebhookUrl}</div>}
                            </div>
                          )}
                        </>
                      )}

                      {form.integracao_boleto !== 'nenhuma' && (
                        <div className={s.formRow}>
                          <div className={s.formGrupo}>
                            <label className={s.label}>Multa (%)</label>
                            <input className={s.input} value={form.boleto_multa_pct} type="number" step="0.01"
                              style={{ width: 90 }} placeholder="2.00"
                              onChange={e => set('boleto_multa_pct', e.target.value)} />
                          </div>
                          <div className={s.formGrupo}>
                            <label className={s.label}>Juros ao mês (%)</label>
                            <input className={s.input} value={form.boleto_juros_mes_pct} type="number" step="0.01"
                              style={{ width: 90 }} placeholder="1.00"
                              onChange={e => set('boleto_juros_mes_pct', e.target.value)} />
                          </div>
                        </div>
                      )}
                      {form.integracao_boleto === 'cnab400' && (
                        <div className={s.formRow}>
                          <div className={s.formGrupo} style={{ flex: 1 }}>
                            <label className={s.label}>Instruções do boleto</label>
                            <input className={`${s.input} ${s.inputLarge}`} value={form.boleto_instrucoes}
                              placeholder="Ex.: Não receber após o vencimento."
                              onChange={e => set('boleto_instrucoes', e.target.value)} />
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {modo === 'editar' && sel && (
                    <div className={s.formRow} style={{ marginTop: 4 }}>
                      <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input type="checkbox" checked={form.ativo !== false && sel.ativo}
                          onChange={e => set('ativo', e.target.checked)} />
                        Conta ativa
                      </label>
                    </div>
                  )}
                </div>

                <div className={s.rodape} style={{ marginTop: 0, borderTop: 'none', paddingLeft: 0 }}>
                  <button className={s.btnErp} onClick={cancelar}>Cancelar</button>
                  <button className={s.btnErpPrimario} onClick={salvar} disabled={salvando}>
                    {salvando ? 'Salvando...' : '✔ Salvar'}
                  </button>
                  {modo === 'editar' && sel && (
                    <button className={s.btnErp}
                      style={{ marginLeft: 'auto', color: sel.ativo ? '#b91c1c' : '#065f46' }}
                      onClick={() => toggleAtivo(sel)}>
                      {sel.ativo ? 'Desativar' : 'Ativar'}
                    </button>
                  )}
                </div>
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
}
